/**
 * Data Upload Configuration Utilities for SDM Web UI
 * Essential functions for SubID and Dataset ID extraction and validation
 */

/**
 * Extract SubID and Dataset ID from filename
 * @param {string} filename - The filename to extract from
 * @returns {Object|null} - Object with subid and datasetId, or null if extraction fails
 */
export function extractSubidAndDatasetId(filename) {
  try {
    if (!filename || typeof filename !== 'string') {
      console.log('extractSubidAndDatasetId: Invalid filename input:', filename);
      return null;
    }
    
    // Remove file extension first
    const nameWithoutExtension = filename.split('.').slice(0, -1).join('.');
    console.log('extractSubidAndDatasetId: Name without extension:', nameWithoutExtension);
    
    // Extract SubID and Dataset ID
    // Pattern: ^(\d{3,6})_(\d{3})
    // - ^(\d{3,6}): Start with 3-6 digits (SubID)
    // - _(\d{3}): Followed by underscore and exactly 3 digits (Dataset ID)
    // - Allow any additional text after the dataset ID (like " copy", " _Alc", etc.)
    const pattern = /^(\d{3,6})_(\d{3})/;
    const match = nameWithoutExtension.match(pattern);
    console.log('extractSubidAndDatasetId: Pattern match result:', match);
    
    if (match) {
      const result = {
        subid: match[1],
        datasetId: match[2]
      };
      console.log('extractSubidAndDatasetId: Successfully extracted:', result);
      return result;
    }
    
    console.log('extractSubidAndDatasetId: No pattern match found');
    return null;
  } catch (error) {
    console.error('Error extracting SubID and Dataset ID:', error);
    return null;
  }
}

/**
 * Extract SubID from input string
 * @param {string} inputString - The input string to extract SubID from
 * @param {boolean} validate - Whether to validate the extracted SubID
 * @returns {string} - The extracted SubID or empty string
 */
export function extractSubid(inputString, validate = true) {
  try {
    if (!inputString || typeof inputString !== 'string') {
      throw new Error('Input string is required and must be a string');
    }

    const pattern = /^(\d{3,6})/;
    const match = inputString.match(pattern);
    
    if (match) {
      const subid = match[1];
      if (validate) {
        return isSubidValid(subid) ? subid : '';
      } else {
        return subid;
      }
    } else {
      return '';
    }
  } catch (error) {
    console.error('Error extracting SubID:', error);
    throw new Error(`Failed to extract SubID from "${inputString}": ${error.message}`);
  }
}

/**
 * Validate SubID
 * @param {string} subid - The SubID to validate
 * @returns {boolean} - Whether the SubID is valid
 */
export function isSubidValid(subid) {
  try {
    if (!subid) {
      return false;
    }
    
    const subidStr = String(subid);
    const isValid = (subidStr.length > 2) && (subidStr.length < 7) && /^\d+$/.test(subidStr);
    
    if (!isValid) {
      console.warn(`Invalid SubID format: "${subid}". Must be 3-6 digits.`);
    }
    
    return isValid;
  } catch (error) {
    console.error('Error validating SubID:', error);
    return false;
  }
}
